import { useQuery } from '@tanstack/react-query';
import {
  Box,
  FormattedDateTime,
  Icon,
  InlineStack,
  SkeletonShape,
  Stack,
  Text,
} from '@customerio/pluma-components/react';
import { SmsMessagePreview } from '@/features/delivery-detail/ui';
import { deliveryKeys } from '@/services/db/delivery/entity';
import { getDeliveryDetailRead, getDeliveryQueryOptions } from '@/services/db/delivery/queries';
import { isRecord } from '@/utils/objects';
import { isEmptyString } from '@/utils/string';
import { inboundChannelIcon, inboundChannelLabel, isWhatsAppInbound } from '../inbound-channels';
import type { ActivityLog } from '../types';

/**
 * The expanded-row preview of an inbound message (Ember `event-previews/inbound-message`): the
 * message the person sent, preceded by the outbound message it replies to when the log names one
 * (`in_reply_to`). WhatsApp inbounds skip the outbound message — its preview renders as plain SMS
 * text, which misrepresents a WhatsApp message — and show only the reply.
 */

const THREAD_MAX_WIDTH = '800px';
const BUBBLE_PLACEHOLDER_STYLE = { height: '40px', width: '342px', borderRadius: '14px' };

function attrString(log: ActivityLog, key: string): string | null {
  const value = isRecord(log.attrs) ? log.attrs[key] : undefined;
  return typeof value === 'string' ? value : null;
}

type ThreadMessageProps = {
  side: 'outbound' | 'inbound';
  body?: string;
  timestamp?: number | null;
  isLoading?: boolean;
  /** Message retention hides the delivery's content, as the delivery views do (`hide_body`). */
  isHidden?: boolean;
};

function ThreadMessage({ side, body, timestamp, isLoading = false, isHidden = false }: ThreadMessageProps) {
  const alignInline = side === 'outbound' ? 'flex-start' : 'flex-end';
  return (
    <Stack gap="050" alignInline={alignInline} data-test-inbound-thread-message={side}>
      {isLoading ? (
        <SkeletonShape style={BUBBLE_PLACEHOLDER_STYLE} />
      ) : isHidden ? (
        <Text size="xs" color="secondary" fontStyle="italic">
          Message content is hidden
        </Text>
      ) : (
        <SmsMessagePreview body={body ?? ''} alignInline={alignInline} />
      )}
      {timestamp != null && timestamp > 0 ? (
        <Text as="span" size="xxs" color="secondary">
          <FormattedDateTime date={timestamp} format="medium_with_time" withRelativeDateFromNow />
        </Text>
      ) : null}
    </Stack>
  );
}

type InboundMessagePreviewProps = {
  log: ActivityLog;
  /** The resolved numeric workspace id; the delivery reads are addressed to it. */
  workspaceId: number;
};

export function InboundMessagePreview({ log, workspaceId }: InboundMessagePreviewProps) {
  const inReplyTo = attrString(log, 'in_reply_to');
  const showReply = !isEmptyString(inReplyTo) && !isWhatsAppInbound(log.name);
  const deliveryId = showReply ? inReplyTo : '';

  // Both reads tolerate failure (no retry): a missing delivery shows the error, not a broken row.
  const deliveryQuery = useQuery({
    ...getDeliveryQueryOptions(workspaceId, deliveryId),
    enabled: showReply,
    retry: false,
  });
  const detailQuery = useQuery({
    queryKey: deliveryKeys.detailContent(workspaceId, deliveryId),
    queryFn: () => getDeliveryDetailRead(workspaceId, deliveryId),
    enabled: showReply,
    retry: false,
  });

  const error = showReply ? (deliveryQuery.error ?? detailQuery.error) : null;
  if (error != null) {
    return (
      <Text color="critical" display="block">
        Error: {error.message}
      </Text>
    );
  }

  return (
    <Box>
      <Stack gap="100" mx="auto" style={{ maxWidth: THREAD_MAX_WIDTH }}>
        <InlineStack alignBlock="center" gap="050">
          <Icon name={inboundChannelIcon(log.name)} size="sm" color="secondary" />
          <Text size="xs" color="secondary">
            {inboundChannelLabel(log.name)} message
          </Text>
        </InlineStack>
        <Stack gap="050">
          {showReply ? (
            <ThreadMessage
              side="outbound"
              isLoading={deliveryQuery.isPending || detailQuery.isPending}
              body={detailQuery.data?.detail.body}
              isHidden={detailQuery.data?.detail.hide_body === true}
              timestamp={deliveryQuery.data?.created}
            />
          ) : null}
          <ThreadMessage side="inbound" body={attrString(log, 'body') ?? ''} timestamp={log.timestamp} />
        </Stack>
      </Stack>
    </Box>
  );
}
